import { LoadingSpinner } from "@bluefalcon/components-package";
import { Box, Button, Flex, Input, Table, TableCaption, Tbody, Td, Th, Thead, Tr, useDisclosure, useEditable } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { stateService } from "../../services";
import { County, CountyCreateRequest, CountyUpdateRequest, State } from "@bluefalcon/clients-management-client-fetch";
import { AddEditCountyModal } from "../../modals/add-edit-county.modal";

type CountiesTableProps = {
    state: State;
    onChange: (state?: State) => void;
};

export const CountiesTable = ({
    state,
    onChange,
}: CountiesTableProps) => {

    const [counties, setCounties] = useState(state.counties);
    const [isLoading, setIsLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedCounty, setSelectedCounty] = useState<County>();
    const [query, setQuery] = useState('');

    const onAddButtonClick = () => {
        setSelectedCounty(undefined);
        onOpen();
    };

    const close = () => {
        setSelectedCounty(undefined);
        onClose();
    };

    const onCountyClick = (county: County) => {
        setSelectedCounty(county);
        onOpen();
    };

    const onCreateCounty = async (req: CountyCreateRequest) => {
        try {
            setIsLoading(true);
            const res = await stateService.updateStateById(state.id, {
                county: { add: [{ stateId: state.id, value: req.value.trim() }] },
            });
            onChange(res);
        } catch (error) { } finally {
            setIsLoading(false);
            close();
        }
    };

    const onUpdateCounty = async (id: number, req: CountyUpdateRequest) => {
        try {
            setIsLoading(true);
            const res = await stateService.updateStateById(state.id, {
                county: {
                    update: [{ id, value: req.value }],
                },
            });
            onChange(res);
        } catch (error) { } finally {
            setIsLoading(false);
            close();
        }
    };

    const onDeleteCounty = async (id: number) => {
        try {
            setIsLoading(true);
            const res = await stateService.updateStateById(state.id, {
                county: {
                    remove: [id],
                },
            });
            onChange(res);
        } catch (error) { } finally {
            setIsLoading(false);
            close();
        }
    };

    const search = (e?: any) => {
        e?.preventDefault();
        const filtered = state.counties.filter((c) => c.value.includes(query));
        setCounties(filtered);
    };

    useEffect(() => {
        search();
    }, [query]);

    if (isLoading) {
        return <LoadingSpinner />
    }

    return (
        <>
            <Flex mb={2}>
                <form onSubmit={search}>
                    <Flex>
                        <Input onChange={(e) => setQuery(e.target.value)} value={query} placeholder="search..." type={'search'} />
                        <Button type="submit" onClick={search} ml={2} colorScheme="primary">Search</Button>
                    </Flex>
                </form>
                <Button onClick={onAddButtonClick} ml={'auto'} colorScheme="teal">Add County</Button>
            </Flex>
            {counties.length ? <Table variant="simple">
                <TableCaption textAlign={'left'}>Total Counties: {state.counties.length}</TableCaption>
                <Thead>
                    <Tr>
                        <Th>ID</Th>
                        <Th>Name</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {counties.map((county) => (
                        <Tr key={county.id}>
                            <Td>{county.id}</Td>
                            <Td>{county.value}</Td>
                            <Td>
                                <Flex>
                                    <Button onClick={() => onCountyClick(county)} mr={2} size="sm">
                                        Edit
                                    </Button>
                                </Flex>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table> : <Box textAlign={'center'} mt={5}>
                No Counties found.
            </Box>}
            <AddEditCountyModal
                closeModal={close}
                isOpen={isOpen}
                county={selectedCounty}
                handleAddCounty={onCreateCounty}
                handleEditCounty={onUpdateCounty}
                handleDeleteCounty={onDeleteCounty}
                stateId={state.id}
            />
        </>
    )
};
